import { useTranslation } from 'react-i18next';

import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './DeviceEventTimeline.module.css';
import type { DeviceEvent } from '../../lib/api/devices';

/**
 * A device's lifecycle trail — `DeviceEventResource`, rendered NEWEST FIRST.
 *
 * `DeviceDetail` hands this component whatever order `events` arrived in
 * (`Sensor.events`/`Reader.events`, present only on the detail response —
 * see `devices.ts`'s own docblock) and this component re-sorts on
 * `occurred_at` itself rather than trusting the wire order, because the one
 * property under test (D-4's own brief) is the ORDER OF WHAT RENDERS, not
 * what the API happened to send. A `null` `occurred_at` (`DeviceEventRecorder`'s
 * "registered" event, per `devices.ts`) sorts to the end — a device that has
 * never had its clock set is not "the newest".
 *
 * ## The reason is the whole point of D-4
 *
 * `status_corrected` (D-4's transition to `in_stock`) is refused by the API
 * without a `reason` (`sensorsApi.setStatus`'s own docblock, and
 * `DeviceStatusDialog`'s own gate). An event row that rendered the label
 * alone would hide the only thing that explains why a device is back in
 * stock rather than assigned or dispensed — `reason` is therefore rendered
 * for ANY event that carries one, not only `status_corrected`, but the one
 * transition guaranteed to carry one is this.
 */

export interface DeviceEventTimelineProps {
  events: readonly DeviceEvent[];
}

function occurredAtMillis(event: DeviceEvent): number {
  if (event.occurred_at === null) return -Infinity;
  const parsed = Date.parse(event.occurred_at);
  return Number.isNaN(parsed) ? -Infinity : parsed;
}

function sortNewestFirst(events: readonly DeviceEvent[]): readonly DeviceEvent[] {
  return [...events].sort((a, b) => occurredAtMillis(b) - occurredAtMillis(a));
}

export function DeviceEventTimeline({ events }: DeviceEventTimelineProps) {
  const { t, i18n } = useTranslation();
  const ordered = sortNewestFirst(events);

  if (ordered.length === 0) {
    return <p className={styles['empty']}>{t('devices.timelineEmpty')}</p>;
  }

  return (
    <ol className={styles['list']}>
      {ordered.map((event) => (
        <li key={event.uuid} className={styles['item']}>
          {/* The reader's own date format (the raw ISO stays on the hover
              title for anyone correlating with server logs), as every
              other timestamp in the application is shown. */}
          <bdi dir="ltr" className={styles['time']} title={event.occurred_at ?? undefined}>
            {event.occurred_at === null ? t('table.notRecorded') : formatTimestamp(event.occurred_at, i18n.language)}
          </bdi>

          <span className={styles['label']}>
            {t(`devices.event.${event.event}`, { defaultValue: event.event })}
          </span>

          {event.from_status !== null && event.to_status !== null ? (
            <span className={styles['transition']}>
              {t('devices.eventFromTo', {
                from: t(`devices.status.${event.from_status}`, { defaultValue: event.from_status }),
                to: t(`devices.status.${event.to_status}`, { defaultValue: event.to_status }),
              })}
            </span>
          ) : null}

          {event.reason !== null ? (
            <span className={styles['reason']}>{t('devices.eventReason', { reason: event.reason })}</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
